import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { RcFile } from "antd/es/upload";
import { useTranslation } from "react-i18next";
import GETJwtToken from "../../redux/authentication/authentication";
import {
  useDeleteSimulationAssetMutation,
  useLazyListSimulationAssetsQuery,
  useUploadSimulationAssetMutation,
  type SimulationAssetUpload,
} from "../../redux/simulation/simulationAPI";
import type { SimulationAssetT } from "../../flatbuffers/generated/api/simulation-asset";
import { SimulationAssetKind } from "../../flatbuffers/generated/api/simulation-asset-kind";
import type { SimulationCaptureFlowT } from "../../flatbuffers/generated/api/simulation-capture-flow";
import { SimulationFlowTransport } from "../../flatbuffers/generated/api/simulation-flow-transport";
import type { SimulationOpcUaDeviceT } from "../../flatbuffers/generated/api/simulation-opc-ua-device";
import { AdapterProtocol } from "../../flatbuffers/generated/archive/adapter-protocol";
import { formatUuidT } from "../../flatbuffers/display";

/** File extensions the upload accepts; `.xml` is a KONE `<opcdomain>` export. */
export const SIMULATION_UPLOAD_ACCEPT = ".pcap,.pcapng,.cap,.xml";

/**
 * Which Simulation API `kind` a file is uploaded as, from its name: an `.xml`
 * file is an OPC UA domain, anything else a capture. The server checks the
 * content's magic bytes, so a misnamed file is refused there rather than here.
 */
export function uploadKindFor(fileName: string): SimulationAssetUpload {
  return fileName.toLowerCase().endsWith(".xml") ? "opcua_domain" : "capture";
}

/** A byte count as B, KiB or MiB with one decimal. */
export function formatBytes(bytes: bigint | number): string {
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

const text = (value: string | Uint8Array | null): string => String(value ?? "");

interface SimulationFilesTabProps {
  /** The organization whose library is shown, as a dashed UUID string. */
  orgId: string;
}

/** One asset's inspection: the endpoints found in a capture, or the devices in an OPC UA domain. */
function AssetDetails({ asset }: { asset: SimulationAssetT }) {
  const [t] = useTranslation("global");
  const flowColumns: ColumnsType<SimulationCaptureFlowT> = [
    {
      title: t("simulation.vendorAddress"),
      key: "vendor",
      render: (_, flow) => <code>{text(flow.vendorAddress)}</code>,
    },
    {
      title: t("simulation.peerAddress"),
      key: "peer",
      render: (_, flow) => <code>{text(flow.peerAddress)}</code>,
    },
    {
      title: t("simulation.transport"),
      key: "transport",
      render: (_, flow) => SimulationFlowTransport[flow.transport],
    },
    {
      title: t("simulation.protocol"),
      key: "protocol",
      render: (_, flow) =>
        flow.protocolDetected ? (
          <Tag color="blue">{AdapterProtocol[flow.detectedProtocol]}</Tag>
        ) : (
          <Tag>{t("simulation.unknownProtocol")}</Tag>
        ),
    },
    {
      title: t("simulation.packets"),
      key: "packets",
      render: (_, flow) => `${flow.packetsFromVendor} / ${flow.packetsToVendor}`,
    },
    {
      title: t("simulation.bytes"),
      key: "bytes",
      render: (_, flow) =>
        `${formatBytes(flow.bytesFromVendor)} / ${formatBytes(flow.bytesToVendor)}`,
    },
    { title: t("simulation.tcpStreams"), dataIndex: "tcpStreams", key: "streams" },
    {
      title: t("simulation.duration"),
      key: "duration",
      render: (_, flow) => `${(Number(flow.durationMs) / 1000).toFixed(1)} s`,
    },
  ];
  const deviceColumns: ColumnsType<SimulationOpcUaDeviceT> = [
    {
      title: t("simulation.nodePrefix"),
      key: "prefix",
      render: (_, device) => <code>{text(device.nodePrefix)}</code>,
    },
    { title: t("simulation.group"), key: "group", render: (_, device) => text(device.group) },
    { title: t("simulation.server"), key: "server", render: (_, device) => text(device.server) },
    { title: t("simulation.items"), key: "items", render: (_, device) => device.items.length },
  ];

  return (
    <Space direction="vertical" className="generalStyles__width100">
      {asset.warnings.map((warning) => (
        <Alert key={warning} type="warning" showIcon message={warning} />
      ))}
      {asset.kind === SimulationAssetKind.CAPTURE ? (
        <Table
          size="small"
          rowKey={(flow) => `${text(flow.vendorAddress)}>${text(flow.peerAddress)}`}
          columns={flowColumns}
          dataSource={asset.flows}
          pagination={false}
        />
      ) : (
        <Table
          size="small"
          rowKey={(device) => text(device.nodePrefix)}
          columns={deviceColumns}
          dataSource={asset.opcuaDevices}
          pagination={false}
        />
      )}
    </Space>
  );
}

/**
 * The organization's library of Wireshark captures and KONE OPC UA domain
 * files: drag files in to upload them, open one to see what the simulator
 * found in it, or delete it.
 */
function SimulationFilesTab({ orgId }: SimulationFilesTabProps) {
  const [t] = useTranslation("global");
  const [triggerList, { data: assets = [], isFetching }] = useLazyListSimulationAssetsQuery();
  const [upload, { isLoading: isUploading }] = useUploadSimulationAssetMutation();
  const [deleteAsset] = useDeleteSimulationAssetMutation();
  const [selected, setSelected] = useState<SimulationAssetT | null>(null);

  const refresh = useCallback(async () => {
    const token = await GETJwtToken();
    triggerList({ orgId, token });
  }, [orgId, triggerList]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onUpload = async (file: RcFile) => {
    const token = await GETJwtToken();
    try {
      const asset = await upload({ orgId, token, kind: uploadKindFor(file.name), file }).unwrap();
      message.success(t("simulation.uploaded", { name: text(asset.name) }));
      if (asset.warnings.length > 0) {
        message.warning(asset.warnings.join(" "));
      }
      refresh();
    } catch (error) {
      const reason = (error as { data?: { message?: string } })?.data?.message;
      message.error(reason ?? t("simulation.uploadFailed"));
    }
    // The request is ours; returning false stops antd's own XHR upload.
    return false;
  };

  const onDelete = async (asset: SimulationAssetT) => {
    const token = await GETJwtToken();
    try {
      await deleteAsset({ id: formatUuidT(asset.id), token }).unwrap();
      refresh();
    } catch (error) {
      const reason = (error as { data?: { message?: string } })?.data?.message;
      message.error(reason ?? t("simulation.deleteFailed"));
    }
  };

  const columns: ColumnsType<SimulationAssetT> = [
    { title: t("simulation.name"), key: "name", render: (_, asset) => text(asset.name) },
    {
      title: t("simulation.kind"),
      key: "kind",
      render: (_, asset) =>
        asset.kind === SimulationAssetKind.CAPTURE ? (
          <Tag color="geekblue">{t("simulation.capture")}</Tag>
        ) : (
          <Tag color="purple">{t("simulation.opcuaDomain")}</Tag>
        ),
    },
    {
      title: t("simulation.contents"),
      key: "contents",
      render: (_, asset) =>
        asset.kind === SimulationAssetKind.CAPTURE
          ? t("simulation.flowCount", { count: asset.flows.length })
          : t("simulation.deviceCount", { count: asset.opcuaDevices.length }),
    },
    {
      title: t("simulation.size"),
      key: "size",
      render: (_, asset) => formatBytes(asset.sizeBytes),
    },
    {
      title: t("simulation.uploadedBy"),
      key: "uploaded",
      render: (_, asset) => `${text(asset.createdBy)} · ${text(asset.createdAt)}`,
    },
    {
      title: t("simulation.actions"),
      key: "actions",
      render: (_, asset) => (
        <Space>
          <Button size="small" onClick={() => setSelected(asset)}>
            {t("simulation.details")}
          </Button>
          <Popconfirm
            title={t("simulation.deleteFileConfirm", { name: text(asset.name) })}
            onConfirm={() => onDelete(asset)}
          >
            <Button size="small" danger>
              {t("simulation.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" className="generalStyles__width100">
      <Upload.Dragger
        accept={SIMULATION_UPLOAD_ACCEPT}
        multiple
        showUploadList={false}
        beforeUpload={onUpload}
        disabled={isUploading}
      >
        <p>{t("simulation.dropFiles")}</p>
        <p>
          <Tooltip title={t("simulation.dropFilesHint")}>{t("simulation.dropFilesKinds")}</Tooltip>
        </p>
      </Upload.Dragger>
      <Table
        size="small"
        rowKey={(asset) => formatUuidT(asset.id)}
        columns={columns}
        dataSource={assets}
        loading={isFetching || isUploading}
        pagination={false}
      />
      <Drawer
        width={900}
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? text(selected.name) : ""}
      >
        {selected && <AssetDetails asset={selected} />}
      </Drawer>
    </Space>
  );
}

export default SimulationFilesTab;
